// Client Portal API Slice - Client-specific endpoints, vehicles, fleet access
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const clientApiSlice = createApi({
  reducerPath: 'clientApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [
    'Client',
    'Vehicle',
    'Device',
    'Driver',
    'DeviceGroup',
    'Alert',
    'Event',
    'Geofences',
    'SupportTicket',
    'Notification',
    'Broadcast',
  ],
  endpoints: (builder) => ({
    // ========== CLIENT MANAGEMENT ==========
    createClient: builder.mutation({
      query: (clientData) => ({
        url: 'clients/create-client/',
        method: 'POST',
        body: clientData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Client'],
    }),

    fetchClient: builder.query({
      query: (group_id) => `clients/client-details/${group_id}/`,
      providesTags: ['Client'],
    }),

    fetchClientByDevice: builder.query({
      query: (device_id) => `clients/client-details-by-device/${device_id}/`,
      providesTags: ['Client'],
    }),

    // ========== CLIENT FLEET ACCESS (READ-ONLY) ==========
    fetchClientDeviceGroups: builder.query({
      query: () => 'fleet/device-groups/',
      providesTags: ['DeviceGroup'],
      keepUnusedDataFor: 120, // Cache for 2 minutes - groups rarely change
      transformResponse: (response) => {
        // Filter to only show client's own device groups
        // This would be handled by backend permissions, but included for clarity
        return response;
      },
    }),

    fetchClientVehicles: builder.query({
      query: () => 'fleet/vehicles/',
      providesTags: ['Vehicle'],
      keepUnusedDataFor: 60, // Cache for 1 minute - vehicles change occasionally
      transformResponse: (response) => {
        // Filter to only show client's own vehicles
        return response;
      },
    }),

    fetchClientDevices: builder.query({
      query: () => 'fleet/devices/',
      providesTags: ['Device'],
      keepUnusedDataFor: 60, // Cache for 1 minute - devices change occasionally
      transformResponse: (response) => {
        // Filter to only show client's own devices
        return response;
      },
    }),

    // ========== CLIENT DRIVERS ==========
    fetchClientDrivers: builder.query({
      query: (params = {}) => ({
        url: 'fleet/client/drivers/',
        params,
      }),
      providesTags: ['Driver'],
      keepUnusedDataFor: 60, // Cache for 1 minute
      transformResponse: (response) => {
        // Return paginated response structure
        return response;
      },
    }),

    fetchClientDriverDetails: builder.query({
      query: (driverId) => `fleet/client/drivers/${driverId}/`,
      providesTags: (result, error, id) => [{ type: 'Driver', id }],
    }),

    createClientDriver: builder.mutation({
      query: (driverData) => ({
        url: 'fleet/client/drivers/create/',
        method: 'POST',
        body: driverData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Driver'],
    }),

    updateClientDriver: builder.mutation({
      query: ({ driverId, ...driverData }) => ({
        url: `fleet/client/drivers/${driverId}/`,
        method: 'PUT',
        body: driverData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (result, error, { driverId }) => [
        { type: 'Driver', id: driverId },
        'Driver',
      ],
    }),

    deleteClientDriver: builder.mutation({
      query: (driverId) => ({
        url: `fleet/client/drivers/${driverId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Driver'],
    }),

    // Driver-Vehicle Assignment endpoints
    createDriverVehicleAssignment: builder.mutation({
      query: (assignmentData) => ({
        url: 'fleet/client/driver-assignments/',
        method: 'POST',
        body: assignmentData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Driver', 'Vehicle'],
    }),

    deleteDriverVehicleAssignment: builder.mutation({
      query: (assignmentId) => ({
        url: `fleet/client/driver-assignments/${assignmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Driver', 'Vehicle'],
    }),

    fetchDriverVehicleAssignments: builder.query({
      query: (params = {}) => ({
        url: 'fleet/client/driver-assignments/',
        params,
      }),
      providesTags: ['Driver', 'Vehicle'],
    }),

    fetchAvailableDriversForVehicle: builder.query({
      query: (vehicleId) => `fleet/client/vehicles/${vehicleId}/available-drivers/`,
      providesTags: ['Driver'],
    }),

    fetchClientGeofences: builder.query({
      query: () => 'fleet/geofences/',
      providesTags: ['Geofences'],
      transformResponse: (response) => {
        // Filter to only show client's own geofences
        return response;
      },
    }),

    fetchClientVehicleEventHistory: builder.query({
      query: ({ vehicleId, eventType, limit = 10 }) => {
        if (!vehicleId) {
          return { url: 'monitoring/events/list/', params: { page_size: 0 } };
        }
        let url = `fleet/vehicles/${vehicleId}/event-history/`;
        const params = new URLSearchParams();
        if (eventType) params.append('event_type', eventType);
        if (limit) params.append('limit', limit);
        const queryString = params.toString();
        if (queryString) url += `?${queryString}`;
        return url;
      },
      providesTags: ['Event'],
    }),

    // ========== CLIENT MONITORING (READ-ONLY) ==========
    fetchClientAlerts: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/alerts/',
        params: {
          include_suppressed: false, // Clients typically don't see suppressed alerts
          include_profile_context: false,
          ...params,
        },
      }),
      providesTags: ['Alert'],
      keepUnusedDataFor: 30, // Cache for 30 seconds - alerts need to be relatively fresh
      transformResponse: (response) => {
        // Filter to only show client's own alerts
        return response;
      },
    }),

    fetchClientAlertsByDevice: builder.query({
      query: (device_id) => `monitoring/alerts/device/${device_id}/`,
      providesTags: ['Alert'],
      keepUnusedDataFor: 30, // Cache for 30 seconds
    }),

    fetchClientEvents: builder.query({
      query: (queryString) =>
        queryString ? `monitoring/events/list/?${queryString}` : 'monitoring/events/list/',
      providesTags: ['Event'],
      keepUnusedDataFor: 30, // Cache for 30 seconds - events need to be relatively fresh
      transformResponse: (response) => {
        // Filter to only show client's own events
        return response;
      },
    }),

    fetchClientEventDetails: builder.query({
      query: (event_id) => `monitoring/events/${event_id}/`,
      providesTags: (result, error, id) => [{ type: 'Event', id }],
    }),

    // ========== CLIENT REPORTS & ANALYTICS ==========
    // Optimized dashboard summary - single endpoint for all dashboard card data
    fetchClientDashboardSummary: builder.query({
      query: (params = {}) => ({
        url: 'fleet/client/dashboard-summary/',
        params,
      }),
      providesTags: ['Client', 'Vehicle', 'Device', 'Driver', 'Event'],
      // Keep data fresh but don't refetch too aggressively
      keepUnusedDataFor: 60, // 1 minute cache
    }),

    // Legacy endpoints - kept for backward compatibility
    fetchClientFleetSummary: builder.query({
      query: () => 'fleet/client/dashboard-summary/',
      providesTags: ['Client', 'Vehicle', 'Device'],
    }),

    fetchClientAlertSummary: builder.query({
      query: (params = {}) => ({
        url: 'fleet/client/dashboard-summary/',
        params,
      }),
      providesTags: ['Alert'],
    }),

    fetchClientVehicleStatus: builder.query({
      query: () => 'fleet/client-vehicle-status/',
      providesTags: ['Vehicle'],
    }),

    // ========== CLIENT PROFILE MANAGEMENT ==========
    fetchClientProfile: builder.query({
      query: () => 'clients/profile/',
      providesTags: ['Client'],
    }),

    updateClientProfile: builder.mutation({
      query: (profileData) => ({
        url: 'clients/profile/',
        method: 'PUT',
        body: profileData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Client'],
    }),

    // ========== CLIENT NOTIFICATIONS ==========
    fetchClientNotifications: builder.query({
      query: (params = {}) => ({
        url: 'clients/notifications/',
        params: {
          page: params.page || 1,
          page_size: params.page_size || 20,
          ...params,
        },
      }),
      providesTags: ['Client'],
    }),

    markNotificationRead: builder.mutation({
      query: (notification_id) => ({
        url: `clients/notifications/${notification_id}/read/`,
        method: 'POST',
      }),
      invalidatesTags: ['Client'],
    }),

    markAllNotificationsRead: builder.mutation({
      query: () => ({
        url: 'clients/notifications/mark-all-read/',
        method: 'POST',
      }),
      invalidatesTags: ['Client'],
    }),

    // ========== CLIENT PREFERENCES ==========
    fetchClientPreferences: builder.query({
      query: () => 'clients/preferences/',
      providesTags: ['Client'],
    }),

    updateClientPreferences: builder.mutation({
      query: (preferences) => ({
        url: 'clients/preferences/',
        method: 'PUT',
        body: preferences,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Client'],
    }),

    // ========== CLIENT SUPPORT (Using shared backend) ==========
    fetchClientSupportTickets: builder.query({
      query: (params = {}) => ({
        url: 'operations/support-tickets/',
        params,
      }),
      providesTags: ['SupportTicket'],
    }),

    fetchClientSupportTicketDetail: builder.query({
      query: (ticketId) => `operations/support-tickets/${ticketId}/`,
      providesTags: (result, error, id) => [{ type: 'SupportTicket', id }],
    }),

    createSupportTicket: builder.mutation({
      query: (ticketData) => ({
        url: 'operations/support-tickets/',
        method: 'POST',
        body: ticketData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SupportTicket'],
    }),

    updateSupportTicket: builder.mutation({
      query: ({ ticketId, ...data }) => ({
        url: `operations/support-tickets/${ticketId}/`,
        method: 'PATCH',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'SupportTicket', id: ticketId },
        'SupportTicket',
      ],
    }),

    addSupportMessage: builder.mutation({
      query: ({ ticketId, message }) => ({
        url: `operations/support-tickets/${ticketId}/add_message/`,
        method: 'POST',
        body: { message },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (result, error, { ticketId }) => [{ type: 'SupportTicket', id: ticketId }],
    }),

    // Broadcast endpoints
    fetchBroadcasts: builder.query({
      query: (params = {}) => ({
        url: 'operations/broadcasts/',
        params,
      }),
      providesTags: ['Broadcast'],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),

    acknowledgeBroadcast: builder.mutation({
      query: (broadcastId) => ({
        url: `operations/broadcasts/${broadcastId}/acknowledge/`,
        method: 'POST',
      }),
      invalidatesTags: ['Broadcast'],
    }),

    // Notification endpoints (shared system)
    fetchNotifications: builder.query({
      query: (params = {}) => ({
        url: 'operations/notifications/',
        params,
      }),
      providesTags: ['Notification'],
      // Force refetch to prevent stale data
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),

    fetchUnreadNotificationCount: builder.query({
      query: () => 'operations/notifications/unread_count/',
      providesTags: ['Notification'],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),

    markNotificationAsRead: builder.mutation({
      query: (notificationId) => ({
        url: `operations/notifications/${notificationId}/mark_read/`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Notification'],
    }),

    markAllNotificationsAsRead: builder.mutation({
      query: () => ({
        url: 'operations/notifications/mark_all_read/',
        method: 'POST',
      }),
      invalidatesTags: ['Notification'],
    }),

    // ========== CLIENT REPORTING ==========
    generateClientReport: builder.mutation({
      query: (reportParams) => ({
        url: 'clients/reports/generate/',
        method: 'POST',
        body: reportParams,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    fetchClientReports: builder.query({
      query: (params = {}) => ({
        url: 'clients/reports/',
        params,
      }),
      providesTags: ['Client'],
    }),

    downloadClientReport: builder.query({
      query: (report_id) => ({
        url: `clients/reports/${report_id}/download/`,
        responseType: 'blob',
      }),
    }),
  }),
});

// Export hooks for Client Portal components
export const {
  // Client Management
  useCreateClientMutation,
  useFetchClientQuery,
  useFetchClientByDeviceQuery,

  // Client Fleet Access (Read-Only)
  useFetchClientDeviceGroupsQuery,
  useFetchClientVehiclesQuery,
  useFetchClientDevicesQuery,
  useFetchClientDriversQuery,
  useFetchClientDriverDetailsQuery,
  useCreateClientDriverMutation,
  useUpdateClientDriverMutation,
  useDeleteClientDriverMutation,
  useCreateDriverVehicleAssignmentMutation,
  useDeleteDriverVehicleAssignmentMutation,
  useFetchDriverVehicleAssignmentsQuery,
  useFetchAvailableDriversForVehicleQuery,
  useFetchClientGeofencesQuery,
  useFetchClientVehicleEventHistoryQuery,

  // Client Monitoring (Read-Only)
  useFetchClientAlertsQuery,
  useFetchClientAlertsByDeviceQuery,
  useFetchClientEventsQuery,
  useFetchClientEventDetailsQuery,

  // Client Reports & Analytics
  useFetchClientDashboardSummaryQuery,
  useFetchClientFleetSummaryQuery,
  useFetchClientAlertSummaryQuery,
  useFetchClientVehicleStatusQuery,

  // Client Profile Management
  useFetchClientProfileQuery,
  useUpdateClientProfileMutation,

  // Client Notifications
  useFetchClientNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,

  // Client Preferences
  useFetchClientPreferencesQuery,
  useUpdateClientPreferencesMutation,

  // Client Support
  useFetchClientSupportTicketsQuery,
  useFetchClientSupportTicketDetailQuery,
  useCreateSupportTicketMutation,
  useUpdateSupportTicketMutation,
  useAddSupportMessageMutation,

  // Broadcasts
  useFetchBroadcastsQuery,
  useAcknowledgeBroadcastMutation,

  // Notifications
  useFetchNotificationsQuery,
  useFetchUnreadNotificationCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,

  // Client Reporting
  useGenerateClientReportMutation,
  useFetchClientReportsQuery,
  useDownloadClientReportQuery,
} = clientApiSlice;
